import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Building2, Users, IdCard, Activity, ScrollText, MailCheck, Settings, LogOut, Menu, ShieldCheck, CreditCard, LineChart, Landmark, Scale, Nfc, ShoppingBag, LifeBuoy, Newspaper, Plane } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { NotificationBell } from "@/components/NotificationBell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MOD_KEYS = ["overview", "companies", "users", "activity", "audit-logs", "support"];

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [openTickets, setOpenTickets] = useState(0);
  useEffect(() => { api.get("/admin/support/summary").then((r) => setOpenTickets(r.data.open)).catch(() => {}); }, []);

  const allLinks = [
    { to: "/admin", label: t("aNav.overview"), icon: LayoutDashboard, end: true, key: "overview" },
    { to: "/admin/companies", label: t("aNav.companies"), icon: Building2, key: "companies" },
    { to: "/admin/users", label: t("aNav.users"), icon: Users, key: "users" },
    { to: "/admin/support", label: t("sup.nav"), icon: LifeBuoy, key: "support", badge: openTickets },
    { to: "/admin/employees", label: t("aNav.employees"), icon: IdCard, key: "employees" },
    { to: "/admin/activity", label: t("aNav.activity"), icon: Activity, key: "activity" },
    { to: "/admin/audit-logs", label: t("aNav.audit"), icon: ScrollText, key: "audit-logs" },
    { to: "/admin/reminders", label: t("aNav.reminders"), icon: MailCheck, key: "reminders" },
    { to: "/admin/subscriptions", label: t("bill.navBilling"), icon: CreditCard, key: "subscriptions" },
    { to: "/admin/orders", label: t("ord.nav"), icon: ShoppingBag, key: "orders" },
    { to: "/admin/service-orders", label: t("im.ordersNav"), icon: Plane, key: "service-orders" },
    { to: "/admin/immigration-rules", label: t("im.rulesNav"), icon: Scale, key: "immigration-rules" },
    { to: "/admin/subcon-rules", label: "Nachunternehmer-Regeln", icon: Scale, key: "subcon-rules" },
    { to: "/admin/hardware", label: t("ahw.nav"), icon: Nfc, key: "hardware" },
    { to: "/admin/hardware/nfc-tags", label: t("nfc.nav"), icon: Nfc, key: "nfc-tags" },
    { to: "/admin/care/nfc-tags", label: "Pflege NFC/QR", icon: Nfc, key: "care-nfc-tags" },
    { to: "/admin/traffic", label: t("aNav.traffic"), icon: LineChart, key: "traffic" },
    { to: "/admin/reference-data/tax-offices", label: t("tax.nav"), icon: Landmark, key: "tax-offices" },
    { to: "/admin/compliance-news", label: t("ai.newsNav"), icon: Newspaper, key: "compliance-news" },
    { to: "/admin/reference-data/time-compliance", label: t("refd.nav"), icon: Scale, key: "time-compliance" },
    { to: "/admin/settings", label: t("aNav.settings"), icon: Settings, key: "settings" },
  ];
  const links = user?.role === "MODERATOR" ? allLinks.filter((l) => MOD_KEYS.includes(l.key)) : allLinks;

  const Sidebar = () => (
    <div className="flex h-full flex-col bg-navy text-slate-300">
      <div className="flex h-16 items-center gap-2 border-b border-white/10 px-5"><Logo variant="light" /></div>
      <div className="px-5 py-3">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-cyan">
          <ShieldCheck className="h-3 w-3" /> {t("aNav.platformAdmin")}
        </div>
      </div>
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${l.key}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-cyan text-white" : "text-slate-300 hover:bg-white/5"}`}>
            <l.icon className="h-4.5 w-4.5" strokeWidth={1.75} /> <span className="flex-1">{l.label}</span>
            {l.badge > 0 && <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" data-testid="admin-support-badge">{l.badge}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3 text-xs text-slate-400">
        <div className="font-semibold text-white">{user?.first_name} {user?.last_name}</div>
        <div className="mt-0.5">{user?.role?.replace("_", " ")}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block">{Sidebar()}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-navy/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64">{Sidebar()}</aside>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-sidebar-toggle" aria-label="Menu"><Menu className="h-5 w-5 text-navy" /></button>
          <div className="font-heading font-bold text-navy">{t("aNav.console")}</div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-xs font-bold text-white" data-testid="admin-profile-menu">
                  {(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="admin-logout-btn" className="text-red-600"><LogOut className="mr-2 h-4 w-4" /> {t("aNav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6"><Outlet /></main>
      </div>
    </div>
  );
}
